import * as Sentry from '@sentry/react';
import axios from 'axios';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';

import { getStorage, removeStorage, setStorage } from '../utils/auth-utils';
import { Api_routes, server_url } from './API_Routes';

const REQUEST_ID_HEADER = 'X-Request-Id';

const createRequestId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // randomUUID throws outside a secure context; fall through to a local id.
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

const beginRequest = (headers = {}) => {
  const requestId = createRequestId();
  return {
    requestId,
    startedAt: Date.now(),
    headers: {
      ...headers,
      [REQUEST_ID_HEADER]: requestId,
    },
  };
};

const isAbortedRequest = (error) =>
  error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';

// A missing response means the request never reached an HTTP status (drop, timeout, CORS).
// Log a scrubbed payload only — the Axios error object carries the Authorization header.
const reportNetworkFailure = (error, { requestId, url, method, startedAt }) => {
  if (error?.response || isAbortedRequest(error)) return;

  const details = {
    requestId,
    code: error?.code ?? null,
    message: error?.message ?? null,
    url: error?.config?.url || url || null,
    method: (error?.config?.method || method || '').toUpperCase() || null,
    durationMs: Date.now() - startedAt,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
  };

  try {
    const safeError = new Error(details.message || 'Network request failed');
    safeError.name = 'NetworkRequestError';
    Sentry.captureException(safeError, {
      tags: {
        request_id: details.requestId,
        axios_code: details.code || 'unknown',
      },
      extra: details,
      fingerprint: ['api-network-error', details.code || 'unknown'],
    });
  } catch {
    // Telemetry must not change request handling.
  }

  try {
    posthog.capture('api_network_error', details);
  } catch {
    // Telemetry must not change request handling.
  }
};

const postRequest = async (url, data, headers, signal) => {
  const request = beginRequest({
    Authorization: getStorage('token'),
    'Content-Type': 'application/json',
    ...headers,
  });

  return new Promise((resolve, reject) => {
    axios
      .post(url, data, {
        headers: request.headers,
        signal,
      })
      .then((response) => {
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'POST' });
        if (isAbortedRequest(error)) {
          return reject(error);
        }
        if (error.response?.status === 401) {
          removeStorage('token');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          // window.location.reload();
        }
        resolve(error.response?.data);
      });
  });
};

const getRequest = async (url, queryParams, headers, signal) => {
  const request = beginRequest({
    Authorization: getStorage('token'),
    'Content-Type': 'application/json',
    ...headers,
  });

  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        headers: request.headers,
        params: queryParams,
        signal,
      })
      .then((response) => {
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'GET' });
        if (isAbortedRequest(error)) {
          return reject(error);
        }
        if (error.response?.status === 401) {
          removeStorage('token');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          // window.location.reload();
        }
        resolve(error.response?.data);
      });
  });
};

const deleteRequest = async (url, queryParams) => {
  const request = beginRequest({
    Authorization: getStorage('token'),
    'Content-Type': 'application/json',
  });

  return new Promise((resolve, reject) => {
    axios
      .delete(url, {
        headers: request.headers,
        params: queryParams,
      })
      .then((response) => {
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'DELETE' });
        if (error.response?.status === 401) {
          removeStorage('token');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          window.location.reload();
        } else resolve(error.response?.data);
      });
  });
};

const putRequest = async (url, data) => {
  const request = beginRequest({
    Authorization: getStorage('token'),
    'Content-Type': 'application/json',
  });

  return new Promise((resolve, reject) => {
    axios
      .put(url, data, {
        headers: request.headers,
      })
      .then((response) => {
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'PUT' });
        if (error.response?.status === 401) {
          removeStorage('token');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          window.location.reload();
        }
        resolve(error.response?.data);
      });
  });
};

const patchRequest = async (url, data) => {
  const request = beginRequest({
    Authorization: getStorage('token'),
    'Content-Type': 'application/json',
  });

  return new Promise((resolve, reject) => {
    axios
      .patch(url, data, {
        headers: request.headers,
      })
      .then((response) => {
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'PATCH' });
        if (error.response?.status === 401) {
          removeStorage('token');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          window.location.reload();
        }
        resolve(error.response?.data);
      });
  });
};

const getNewToken = async (refreshToken) => {
  const url = server_url + import.meta.env.REACT_APP_AUTH_SERVER_PORT + Api_routes.refreshToken;
  const request = beginRequest({
    'Content-Type': 'application/json',
  });

  return new Promise((resolve, reject) => {
    axios
      .post(
        url,
        {
          refreshToken,
        },
        {
          headers: request.headers,
        }
      )
      .then(async (response) => {
        setStorage('token', response.data.data.token);
        resolve(response?.data);
      })
      .catch(async (error) => {
        reportNetworkFailure(error, { ...request, url, method: 'POST' });
        reject(error.response?.data);
      });
  });
};

const ciIdentify = (data) => {
  window._cio.identify({ ...data });
};

export { postRequest, getRequest, deleteRequest, putRequest, patchRequest, ciIdentify, getNewToken };
